import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Download, Truck, DollarSign, Percent, Clock, Printer, Package, Loader2, X, Sparkles } from "lucide-react";

const STATUS_LABEL = {
  sent_to_rancher: "New — sent to you", processing: "Processing", shipped: "Shipped",
  delivered: "Delivered", canceled: "Canceled", refunded: "Refunded",
};

function Stat({ label, value, icon: Icon, testId, sub }) {
  return (
    <div className="card-flat p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[var(--ff-text-muted)]"><Icon size={14}/><span className="overline">{label}</span></div>
      <p className="font-display text-3xl mt-2 text-[var(--ff-primary)]">{value}</p>
      {sub && <p className="text-xs text-[var(--ff-text-muted)] mt-1">{sub}</p>}
    </div>
  );
}

export function SuborderList({ canFulfill }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tracking_number: "", carrier: "", status: "" });
  const [err, setErr] = useState("");
  const [parcel, setParcel] = useState({ weight: 6, length: 12, width: 10, height: 8 });
  const [rates, setRates] = useState(null);
  const [rateErr, setRateErr] = useState("");
  const [loadingRates, setLoadingRates] = useState(false);
  const [buying, setBuying] = useState("");
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);

  const loadPresets = () => api.get("/farm/box-presets").then(r => setPresets(r.data.presets || [])).catch(() => {});
  useEffect(() => { loadPresets(); }, []);

  const applyPreset = (p) => setParcel({ weight: p.weight, length: p.length, width: p.width, height: p.height });

  const saveCurrentAsPreset = async () => {
    const name = window.prompt("Name this box size (e.g. Small cooler, Bulk box):");
    if (!name) return;
    setSavingPreset(true);
    try {
      const next = [...presets, { name, ...parcel }].slice(0, 12);
      const r = await api.put("/farm/box-presets", { presets: next });
      setPresets(r.data.presets || []);
    } catch { /* noop */ } finally { setSavingPreset(false); }
  };

  const deletePreset = async (id) => {
    const next = presets.filter(p => p.id !== id).map(({ name, weight, length, width, height }) => ({ name, weight, length, width, height }));
    const r = await api.put("/farm/box-presets", { presets: next });
    setPresets(r.data.presets || []);
  };

  const printFulfillment = async (id) => {
    try {
      const r = await api.get(`/farm/suborders/${id}/fulfillment.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* noop */ }
  };

  const closeModal = () => {
    setEditing(null); setForm({ tracking_number: "", carrier: "", status: "" });
    setRates(null); setRateErr(""); setBuying(""); setParcel({ weight: 6, length: 12, width: 10, height: 8 });
  };

  const getRates = async () => {
    setLoadingRates(true); setRateErr(""); setRates(null);
    try {
      const r = await api.post(`/farm/suborders/${editing.id}/label/rates`, parcel);
      setRates((r.data.rates || []).slice().sort((a, b) => a.amount - b.amount));
    } catch (e2) {
      setRateErr(formatApiError(e2.response?.data?.detail) || "Could not fetch rates");
    } finally { setLoadingRates(false); }
  };

  const buyLabel = async (rate) => {
    setBuying(rate.object_id); setRateErr("");
    try {
      await api.post(`/farm/suborders/${editing.id}/label/buy`, {
        rate_object_id: rate.object_id, carrier: rate.provider, service: rate.service, amount: rate.amount,
      });
      closeModal(); load();
    } catch (e2) {
      setRateErr(formatApiError(e2.response?.data?.detail) || "Label purchase failed");
    } finally { setBuying(""); }
  };

  const buyCheapest = async () => {
    setRateErr(""); setBuying("cheapest");
    try {
      const r = await api.post(`/farm/suborders/${editing.id}/label/rates`, parcel);
      const list = (r.data.rates || []).slice().sort((a, b) => a.amount - b.amount);
      setRates(list);
      if (!list.length) { setRateErr("No carrier rates available for this package."); return; }
      const best = list[0];
      if (!window.confirm(`Buy the cheapest label — ${best.provider} ${best.service} for $${best.amount.toFixed(2)}?`)) return;
      await api.post(`/farm/suborders/${editing.id}/label/buy`, {
        rate_object_id: best.object_id, carrier: best.provider, service: best.service, amount: best.amount,
      });
      closeModal(); load();
    } catch (e2) {
      setRateErr(formatApiError(e2.response?.data?.detail) || "Could not buy cheapest label");
    } finally { setBuying(""); }
  };

  const load = () => api.get("/farm/suborders", { params: filter ? { status: filter } : {} }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = {};
      if (form.tracking_number) payload.tracking_number = form.tracking_number;
      if (form.carrier) payload.carrier = form.carrier;
      if (form.status) payload.status = form.status;
      await api.post(`/farm/suborders/${editing.id}/update`, payload);
      closeModal(); load();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Update failed"); }
  };

  return (
    <div data-testid="farm-suborders">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["", "sent_to_rancher", "processing", "shipped", "delivered"].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`suborder-filter-${s || "all"}`}
            className={`px-4 py-1.5 rounded-full text-xs border ${filter === s ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>
            {s ? STATUS_LABEL[s] : "All"}
          </button>
        ))}
      </div>
      <p className="text-xs text-[var(--ff-text-muted)] mb-4">You see only your portion of each order and the shipping details needed to fulfill it. Customers are told orders from multiple ranchers ship separately.</p>
      {rows.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] py-10 text-center" data-testid="suborders-empty">No marketplace orders yet.</p>}
      <div className="space-y-3">
        {rows.map(s => (
          <div key={s.id} className="card-flat p-5" data-testid={`suborder-${s.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-mono text-xs text-[var(--ff-text-muted)]">{s.id} · order {s.order_id.slice(0, 8)}</p>
                <p className="font-medium mt-1">{s.customer_name}</p>
                <p className="text-xs text-[var(--ff-text-muted)]">{s.shipping_address || "Address on packing slip"}</p>
                <ul className="mt-2 text-sm">{s.items.map((it, i) => <li key={i}>{it.qty} × {it.name}</li>)}</ul>
              </div>
              <div className="text-right">
                <span className="inline-block px-3 py-1 rounded-full text-xs bg-[var(--ff-muted)]" data-testid={`suborder-status-${s.id}`}>{STATUS_LABEL[s.status] || s.status}</span>
                <p className="mt-2 text-sm">Subtotal ${s.subtotal.toFixed(2)}</p>
                {s.payout_basis === "wholesale" ? (
                  <p className="text-xs text-[var(--ff-text-muted)]" data-testid={`suborder-wholesale-${s.id}`}>
                    Wholesale payout <strong className="text-[var(--ff-primary)]">${Number(s.payout).toFixed(2)}</strong>
                    {" · "}Platform keeps ${Number(s.platform_margin || 0).toFixed(2)}
                  </p>
                ) : (
                  <p className="text-xs text-[var(--ff-text-muted)]">Commission {s.commission_pct}% · Payout <strong>${s.payout.toFixed(2)}</strong></p>
                )}
                {s.tracking_number && <p className="text-xs mt-1">{s.carrier} {s.tracking_number}</p>}
                {s.label_url && (
                  <a href={s.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs mt-1 underline text-[var(--ff-primary)]" data-testid={`suborder-label-${s.id}`}>
                    <Printer size={12}/> Print label{s.label_cost ? ` ($${Number(s.label_cost).toFixed(2)})` : ""}
                  </a>
                )}
                {canFulfill && !["delivered", "canceled", "refunded"].includes(s.status) && (
                  <button className="btn-secondary mt-3 text-xs" onClick={() => { setEditing(s); setForm({ tracking_number: s.tracking_number || "", carrier: s.carrier || "", status: "" }); }} data-testid={`suborder-update-${s.id}`}>
                    <Truck size={14}/> Ship / add tracking
                  </button>
                )}
                {canFulfill && !["canceled", "refunded"].includes(s.status) && (
                  <button type="button" className="block ml-auto mt-2 inline-flex items-center gap-1 text-xs underline text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)]" onClick={() => printFulfillment(s.id)} data-testid={`suborder-print-${s.id}`}>
                    <Printer size={12}/> Print slip{s.label_url ? " + label" : ""}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <form onSubmit={submit} className="bg-white rounded-xl p-6 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="suborder-modal">
            <h3 className="font-display text-2xl">Ship {editing.id}</h3>

            <div className="rounded-lg border border-[var(--ff-border)] p-4 space-y-3 bg-[var(--ff-muted)]/40" data-testid="label-buy-section">
              <div className="flex items-center gap-2">
                <Package size={16} className="text-[var(--ff-primary)]"/>
                <span className="overline">Buy a prepaid label</span>
              </div>
              {presets.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="box-presets">
                  {presets.map(p => (
                    <span key={p.id} className="group inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-white border border-[var(--ff-border)] text-xs cursor-pointer hover:border-[var(--ff-primary)]"
                      onClick={() => applyPreset(p)} data-testid={`preset-${p.id}`} title={`${p.weight}lb · ${p.length}×${p.width}×${p.height}in`}>
                      {p.name}
                      <button type="button" onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }} className="opacity-40 hover:opacity-100" data-testid={`preset-del-${p.id}`}>
                        <X size={11}/>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-4 gap-2">
                {[["weight","Weight (lb)"],["length","L (in)"],["width","W (in)"],["height","H (in)"]].map(([k,lbl]) => (
                  <label key={k} className="block">
                    <span className="text-[10px] text-[var(--ff-text-muted)] block mb-1">{lbl}</span>
                    <input type="number" min="1" step="0.1" className="input-base !py-1.5 text-sm" value={parcel[k]}
                      onChange={e => setParcel({ ...parcel, [k]: parseFloat(e.target.value) || 0 })} data-testid={`parcel-${k}`}/>
                  </label>
                ))}
              </div>
              <button type="button" onClick={saveCurrentAsPreset} disabled={savingPreset || presets.length >= 12} className="text-[11px] text-[var(--ff-primary)] underline disabled:opacity-40" data-testid="save-preset-btn">
                Save these dimensions as a box preset
              </button>
              <button type="button" onClick={getRates} disabled={loadingRates} className="btn-secondary text-sm w-full justify-center" data-testid="get-rates-btn">
                {loadingRates ? <><Loader2 size={14} className="animate-spin"/> Fetching live rates…</> : <>Get carrier rates</>}
              </button>
              <button type="button" onClick={buyCheapest} disabled={!!buying || loadingRates} className="btn-primary text-sm w-full justify-center" data-testid="buy-cheapest-btn">
                {buying === "cheapest" ? <><Loader2 size={14} className="animate-spin"/> Finding cheapest…</> : <><Sparkles size={14}/> Buy cheapest label (1-tap)</>}
              </button>
              {rateErr && <p className="text-xs text-rose-700" data-testid="label-error">{rateErr}</p>}
              {rates && rates.length > 0 && (
                <div className="space-y-2" data-testid="rate-list">
                  {rates.map((r, i) => {
                    const baseMargin = Number(editing?.platform_margin || 0);
                    const after = Math.round((baseMargin - r.amount) * 100) / 100;
                    const cheapest = i === 0;
                    return (
                    <div key={r.object_id} className={`flex items-center justify-between gap-2 border rounded-lg p-2.5 bg-white text-sm ${cheapest ? "border-[var(--ff-primary)] ring-1 ring-[var(--ff-primary)]/40" : "border-[var(--ff-border)]"}`} data-testid={`rate-${r.object_id}`}>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {r.provider} <span className="font-normal text-[var(--ff-text-muted)]">{r.service}</span>
                          {cheapest && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--ff-primary)] text-white" data-testid="rate-cheapest-badge"><Sparkles size={9}/> Cheapest · best margin</span>}
                        </p>
                        <p className="text-[11px] text-[var(--ff-text-muted)]">{r.estimated_days ? `~${r.estimated_days} day(s)` : "Est. varies"} · ${r.amount.toFixed(2)}</p>
                        <p className={`text-[11px] font-medium ${after < 0 ? "text-rose-700" : "text-[var(--ff-primary)]"}`} data-testid={`rate-margin-${r.object_id}`}>
                          {after < 0 ? `⚠ Margin goes negative: -$${Math.abs(after).toFixed(2)}` : `Margin after label: $${after.toFixed(2)}`}
                        </p>
                      </div>
                      <button type="button" onClick={() => buyLabel(r)} disabled={!!buying} className={`text-xs whitespace-nowrap ${cheapest ? "btn-primary" : "btn-secondary"}`} data-testid={`buy-rate-${r.object_id}`}>
                        {buying === r.object_id ? <Loader2 size={13} className="animate-spin"/> : `Buy $${r.amount.toFixed(2)}`}
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
              {rates && rates.length === 0 && <p className="text-xs text-[var(--ff-text-muted)]">No rates returned — adjust dimensions or enter tracking manually below.</p>}
            </div>

            <div className="relative text-center"><span className="text-[11px] text-[var(--ff-text-muted)] bg-white px-2 relative z-10">or enter tracking manually</span><div className="absolute inset-x-0 top-1/2 border-t border-[var(--ff-border)]"/></div>

            <label className="block"><span className="overline">Carrier</span>
              <input className="input-base mt-2" placeholder="UPS, FedEx, USPS" value={form.carrier} onChange={e => setForm({...form, carrier: e.target.value})} data-testid="suborder-carrier"/></label>
            <label className="block"><span className="overline">Tracking number</span>
              <input className="input-base mt-2" value={form.tracking_number} onChange={e => setForm({...form, tracking_number: e.target.value})} data-testid="suborder-tracking"/></label>
            <label className="block"><span className="overline">Status</span>
              <select className="input-base mt-2" value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="suborder-status-select">
                <option value="">{`Keep current${form.tracking_number ? " (auto-ships when tracking added)" : ""}`}</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="canceled">Cancel</option>
              </select></label>
            {err && <p className="text-sm text-rose-700" data-testid="suborder-error">{err}</p>}
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="suborder-save">Save tracking</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default function FinanceTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/farm/finance").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-sm text-[var(--ff-text-muted)]">Loading finance…</p>;
  const t = data.totals;
  const download = async () => {
    const r = await api.get("/farm/finance/statement.csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "statement.csv"; a.click();
  };
  return (
    <div className="space-y-6" data-testid="farm-finance-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl">Commissions &amp; payouts</h3>
          <p className="text-sm text-[var(--ff-text-muted)]">Your marketplace commission rate is <strong data-testid="finance-commission-pct">{data.commission_pct}%</strong>{data.is_custom_rate ? " (negotiated rate)" : ""}. Payouts release <strong>automatically</strong> {data.payout_hold_days} days after delivery — sent to your bank via Stripe once you connect payouts, or held in your ledger balance until then.</p>
        </div>
        <button onClick={download} className="btn-secondary" data-testid="finance-download-statement"><Download size={14}/> Download statement</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Gross sales" value={`$${t.gross_sales.toFixed(2)}`} icon={DollarSign} testId="finance-gross"/>
        {t.wholesale_payout > 0
          ? <Stat label="Platform margin" value={`$${(t.platform_margin || 0).toFixed(2)}`} icon={Percent} testId="finance-platform-margin"/>
          : <Stat label="Farm To You commission" value={`-$${t.commission.toFixed(2)}`} icon={Percent} testId="finance-commission"/>}
        <Stat label="Pending payout" value={`$${t.payout_pending.toFixed(2)}`} icon={Clock} testId="finance-pending"/>
        <Stat label="Paid out" value={`$${t.payout_paid.toFixed(2)}`} icon={DollarSign} testId="finance-paid"/>
        {t.label_spend_total > 0 && (
          <Stat label="Label spend (this month)" value={`$${(t.label_spend_month || 0).toFixed(2)}`} icon={Truck} testId="finance-label-spend"
                sub={`$${(t.label_spend_total || 0).toFixed(2)} all-time`}/>
        )}
      </div>
      {data.label_cap > 0 && (() => {
        const spent = Number(t.label_spend_today || 0);
        const cap = Number(data.label_cap);
        const pct = Math.min(100, Math.round((spent / cap) * 100));
        const remaining = Math.max(0, cap - spent);
        const barColor = pct >= 100 ? "var(--ff-danger, #be123c)" : pct >= 75 ? "#b45309" : "var(--ff-primary)";
        return (
          <div className="card-flat p-5" data-testid="finance-label-cap-meter">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[var(--ff-text-muted)]"><Truck size={14}/><span className="overline">Shipping labels today</span></div>
              <span className="text-sm font-medium" data-testid="label-cap-values">${spent.toFixed(2)} <span className="text-[var(--ff-text-muted)]">/ ${cap.toFixed(2)} daily limit</span></span>
            </div>
            <div className="mt-3 h-2.5 rounded-full bg-[var(--ff-muted)] overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} data-testid="label-cap-bar"/>
            </div>
            <p className="text-xs text-[var(--ff-text-muted)] mt-2" data-testid="label-cap-note">
              {remaining > 0
                ? `$${remaining.toFixed(2)} of label spend left today. Resets at midnight UTC.`
                : "Daily label limit reached — it resets at midnight UTC. Contact support to raise your limit."}
            </p>
            {Array.isArray(data.label_spend_7d) && data.label_spend_7d.length > 0 && (() => {
              const wk = data.label_spend_7d;
              const anySpend = wk.some(d => d.amount > 0);
              const peak = Math.max(cap, ...wk.map(d => d.amount), 1);
              const dow = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
              return (
                <div className="mt-4 pt-3 border-t border-[var(--ff-border)]" data-testid="label-cap-sparkline">
                  <p className="text-[10px] uppercase tracking-wide text-[var(--ff-text-muted)] mb-2">Last 7 days</p>
                  {!anySpend ? (
                    <p className="text-xs text-[var(--ff-text-muted)] py-3" data-testid="label-cap-sparkline-empty">No label spend in the last 7 days.</p>
                  ) : (
                    <div className="flex items-end gap-1.5 h-16">
                      {wk.map((d, i) => {
                        const h = Math.max(2, Math.round((d.amount / peak) * 100));
                        const isToday = i === wk.length - 1;
                        const label = dow[new Date(d.date + "T00:00:00").getDay()];
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full" title={`${d.date}: $${d.amount.toFixed(2)}`} data-testid={`spark-day-${i}`}>
                            <div className={`w-full rounded-t transition-all ${isToday ? "bg-[var(--ff-primary)]" : "bg-[var(--ff-primary)]/35"}`} style={{ height: `${h}%` }}/>
                            <span className="text-[9px] text-[var(--ff-text-muted)] mt-1">{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })()}
      <div className="card-flat p-5">
        <p className="overline mb-3">Who covers what</p>
        <div className="grid sm:grid-cols-4 gap-3 text-sm">
          {Object.entries(data.fee_policy).map(([k, v]) => (
            <div key={k} data-testid={`fee-policy-${k}`}><p className="text-[var(--ff-text-muted)] capitalize">{k.replace("_", " ")}</p><p className="font-medium">{v === "platform" ? "Farm To You" : "Rancher"}</p></div>
          ))}
        </div>
      </div>
      <div className="card-flat p-5" data-testid="finance-upcoming-payouts">
        <p className="overline mb-3">Upcoming payouts</p>
        {(!data.upcoming_payouts || data.upcoming_payouts.length === 0) && <p className="text-sm text-[var(--ff-text-muted)]">No pending payouts. Delivered orders release {data.payout_hold_days} days after delivery.</p>}
        <div className="space-y-2">
          {(data.upcoming_payouts || []).map(p => {
            const rel = p.release_date ? new Date(p.release_date) : null;
            const label = p.state === "awaiting_delivery"
              ? "Releases after delivery"
              : p.ready ? "Due now — releasing on next run"
              : `Releases ${rel.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
            const tone = p.state === "awaiting_delivery" ? "bg-stone-100 text-stone-700"
              : p.ready ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200";
            return (
              <div key={p.suborder_id} className="flex items-center justify-between gap-3 border-t border-[var(--ff-border)] pt-2 text-sm" data-testid={`finance-upcoming-${p.suborder_id}`}>
                <span className="font-mono text-xs text-[var(--ff-text-muted)]">order {(p.order_id || "").slice(0, 8)}</span>
                <span className="font-semibold">${Number(p.payout).toFixed(2)}</span>
                <span className={`inline-flex items-center text-[11px] px-2.5 py-0.5 rounded-full ${tone}`}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm" data-testid="finance-ledger">
          <thead className="bg-[var(--ff-muted)] text-left"><tr><th className="p-3">Date</th><th className="p-3">Order</th><th className="p-3">Type</th><th className="p-3 text-right">Amount</th></tr></thead>
          <tbody>
            {data.ledger.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[var(--ff-text-muted)]">No ledger entries yet.</td></tr>}
            {data.ledger.map(l => (
              <tr key={l.id} className="border-t border-[var(--ff-border)]">
                <td className="p-3">{(l.created_at || "").slice(0, 10)}</td><td className="p-3 font-mono text-xs">{(l.order_id || "").slice(0, 8)}</td>
                <td className="p-3 capitalize">{l.type.replace("_", " ")}{l.pct ? ` (${l.pct}%)` : ""}</td>
                <td className={`p-3 text-right ${l.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>{l.amount < 0 ? "-" : "+"}${Math.abs(l.amount).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
